import { cn } from '@/lib/utils';
import { useCallback, useEffect, useState } from 'react';

type NumericControlSize = 'sm' | 'lg';

interface NumericControlProps {
  value: number | null;
  onChange: (value: number | null) => void;
  disabled?: boolean;
  unit?: string;
  /** Visual size variant. `sm` (default) for compact tree-editor rows;
   *  `lg` for detail panels (full-width, taller). */
  size?: NumericControlSize;
  placeholder?: string;
}

/** Allowed-while-typing pattern: optional minus, digits, single separator. */
const ALLOWED_INPUT = /^-?\d*[.,]?\d*$/;

/**
 * Parse user input to a numeric value.
 * Supports both comma and dot as decimal separator.
 */
function parseUserInputToNumber(input: string): number | null {
  if (input === '' || input === '-') return null;
  const v = parseFloat(input.replace(',', '.'));
  return isNaN(v) ? null : v;
}

/** Format a stored value for display. Non-number values render empty. */
function formatValueForInput(value: number | null): string {
  if (typeof value !== 'number') return '';
  return value.toLocaleString(undefined, { useGrouping: false });
}

/**
 * Number input with optional unit badge.
 *
 * Behavior (uniform across sizes):
 *  - Regex-gated typing (rejects invalid in-progress input).
 *  - Commit-on-blur and commit-on-Enter (no per-keystroke onChange).
 *  - Separator-aware parsing (comma or dot).
 *  - Syncs from external value changes.
 *
 * Visual variants:
 *  - `sm` (default): w-[100px], h-8, smaller text/padding (tree editor).
 *  - `lg`: w-full, h-12, base text, larger padding (question detail panel).
 */
export function NumericControl({
  value,
  onChange,
  disabled,
  unit,
  size = 'sm',
  placeholder,
}: NumericControlProps) {
  const [localValue, setLocalValue] = useState(formatValueForInput(value));

  // Sync from external value changes (e.g. undo/revert, type change)
  useEffect(() => {
    const parsed = parseUserInputToNumber(localValue);
    if (parsed !== value) {
      setLocalValue(formatValueForInput(value));
    }
    // Only sync when value prop changes, not when localValue changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    // Reject anything that isn't a valid in-progress numeric string
    if (!ALLOWED_INPUT.test(raw)) return;
    setLocalValue(raw);
  }, []);

  const commit = useCallback(() => {
    const parsed = parseUserInputToNumber(localValue);
    if (parsed !== value) onChange(parsed);
  }, [localValue, value, onChange]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        commit();
        (e.target as HTMLInputElement).blur();
      }
    },
    [commit],
  );

  const handleClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
  }, []);

  const isLg = size === 'lg';

  return (
    <div
      className={cn(
        'flex items-center',
        isLg ? 'w-full gap-0' : 'w-[100px] shrink-0',
      )}
    >
      <input
        type="text"
        inputMode="decimal"
        value={localValue}
        onChange={handleChange}
        onBlur={commit}
        onKeyDown={handleKeyDown}
        onClick={handleClick}
        disabled={disabled}
        placeholder={placeholder}
        className={cn(
          'border-input w-full border text-right',
          isLg
            ? 'border-border h-12 rounded-lg px-4 text-base focus-visible:ring-2'
            : 'h-8 rounded-md px-2 text-sm focus-visible:ring-1',
          'focus-visible:ring-ring focus-visible:outline-none',
          disabled && 'cursor-not-allowed opacity-50',
          unit &&
            (isLg ? 'rounded-r-none border-r-0' : 'rounded-r-none border-r-0'),
        )}
        aria-label="Numeric value"
      />
      {unit && (
        <span
          className={cn(
            'bg-muted text-muted-foreground border-input flex items-center border',
            isLg
              ? 'border-border h-12 rounded-r-lg px-4 text-base'
              : 'h-8 rounded-r-md px-2 text-sm',
          )}
        >
          {unit}
        </span>
      )}
    </div>
  );
}
